import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import SectionCard from './SectionCard';
import KpiStat from './KpiStat';
import StatusBar from './StatusBar';
import useModuleStats from './useModuleStats';
import RecentItems from './RecentItems';
import { projectStatusLabels } from './statusLabels';
import { clampPercent, formatCount } from '../overview/format';
import { projectStatusKeys, projectStatusLabel, projectStatusTone } from '../overview/projectStatus';

/** Projects — totals + status distribution. Hidden for roles the endpoint rejects. */
const ProjectsSection: React.FC = () => {
  const { isRTL } = useLanguage();
  const { data, loading, error } = useModuleStats<any>('/projects/statistics/');

  if (error) return null;
  // No records yet: keep the module visible with an empty state rather than hiding it.
  const empty = !loading && (!data || (data.total ?? 0) === 0)
    ? {
        title: isRTL ? 'لا توجد مشاريع حتى الآن' : 'No projects yet',
        text: isRTL ? 'سيظهر هنا توزيع حالات المشاريع وتقدمها بمجرد إنشاء أول مشروع.' : 'Project status and progress will appear here once the first project is created.',
        action: { to: '/projects/new', label: isRTL ? 'إنشاء مشروع' : 'Create project' },
      }
    : null;

  const byStatus: Record<string, number> = data?.by_status || {};
  const onHold = byStatus.on_hold ?? 0;

  return (
    <SectionCard
      icon="bi-kanban"
      title={isRTL ? 'المشاريع' : 'Projects'}
      caption={isRTL ? 'توزيع الحالات وأحدث المشاريع' : 'Status distribution and latest projects'}
      to="/projects"
      loading={loading}
      empty={empty}
      stats={(
        <>
          <KpiStat value={formatCount(data?.total)} label={isRTL ? 'الإجمالي' : 'Total'} />
          <KpiStat value={formatCount(byStatus.active)} label={isRTL ? 'نشطة' : 'Active'} tone="primary" />
          <KpiStat value={formatCount(byStatus.completed)} label={isRTL ? 'مكتملة' : 'Completed'} tone="success" />
          <KpiStat value={formatCount(onHold)} label={isRTL ? 'مُعلقة' : 'On Hold'} tone={onHold > 0 ? 'warning' : 'default'} />
          <KpiStat
            value={`${clampPercent(data?.average_progress)}%`}
            label={isRTL ? 'متوسط تقدم الجارية' : 'Avg. progress (ongoing)'}
          />
        </>
      )}
    >
      <StatusBar
        items={projectStatusKeys(byStatus).map((k) => ({
          key: k,
          label: projectStatusLabel(k, isRTL),
          count: byStatus[k] ?? 0,
          color: projectStatusTone(k).bar,
        }))}
      />
      <RecentItems
        endpoint="/projects/"
        title={isRTL ? 'أحدث المشاريع' : 'Recent Projects'}
        itemLink={(pr) => `/projects/${pr.id}`}
        primary={(pr) => (isRTL && pr.title_ar ? pr.title_ar : pr.title)}
        secondary={(pr) => pr.project_id}
        statusKey={(pr) => pr.status}
        labels={projectStatusLabels(isRTL)}
      />
    </SectionCard>
  );
};

export default ProjectsSection;
